/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useTheme } from 'next-themes';

import { TariffCardPopup } from '@/components/ui/TariffCard/TariffCardPopup';
import { ETheme } from '@/enums/theme';
import { overflowDocument } from '@/utils/index';
import { useWindowSize } from '@/hooks/index';
import DayNightSwitch from '@/ui/DayNightSwitch/DayNightSwitch';
import FindYourTariffModal from '@/ui/FindYourTariffModal/FindYourTariffModal';
import { CloseMobile, Logo, Menu, Search } from '@/icons/index';
import {
  ActiveLink,
  Button,
  InputAutoComplete,
  JoinBackcellModal,
  LoginModal,
  PhoneNumbersDropDown,
  TopUpBalanceModal,
} from '@/ui/index';
import ButtonsSwitch from '@/ui/ButtonsSwitch/ButtonsSwitch';
import BurgerSvg from '@/icons/Burger';
import { useIsCabinet } from '@/hooks/useCabinet';
import { isUserLoggedIn } from '@/hooks/useIsLogged';
import User from '@/icons/User';
import ArrowRightNew from '@/icons/ArrowRightNew';
import { TopUpModal } from '@/components/cabinet/top-up-modal';

import ToolBar from '../ToolBar/ToolBar';
import SearchField from './SearchField/SearchField';
import NavItem from './NavItem/NavItem';
import Language from './Language/Language';
import Portal from '../Portal/Portal';

import styles from './styles.module.scss';

const Header = ({
  navigation,
  toolbar,
  bundle,
  topLinks,
  marqueeData,
  join,
}) => {
  const loggedIn = isUserLoggedIn();
  const route = useRouter();
  const { t } = useTranslation('common');
  const { t: cabinet } = useTranslation('cabinet');
  const [windowsX] = useWindowSize();
  const { push } = useRouter();
  const { theme } = useTheme();
  const [user, setUser] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState(false);
  const [openToolBar, setOpenToolBar] = React.useState(false);
  const [openSearch, setOpenSearch] = React.useState(false);
  const [openLoginModal, setOpenLoginModal] = React.useState(false);
  const [topUpBalanceModal, setTopUpBalanceModal] = React.useState(false);
  const [openJoinBackcellModal, setOpenJoinBackcellModal] =
    React.useState(false);
  const [isBusiness, setIsBusiness] = React.useState(false);
  const [findYourTariffModal, setFindYourTariffModal] = React.useState(false);
  const [findYourTariffModalData, setFindYourTariffModalData] =
    React.useState(null);
  const [topIconTheme, setTopIconTheme] = useState('light_mode_icon');
  const isCabinetPage = useIsCabinet();
  const [topUpModalActive, setTopUpModalActive] = useState(false);

  const handleOpenMenu = () => {
    overflowDocument(!openMenu);
    setOpenMenu(!openMenu);
  };
  const onLogin = () => {
    setTopUpBalanceModal(false);
    setOpenLoginModal(true);
  };

  const handleOpenToolBar = React.useCallback(
    (value) => {
      setOpenMenu(false);
      overflowDocument(false);
      setOpenToolBar(value || !openToolBar);
      if (value) {
        document.body.classList.add('no-scroll');
      } else {
        document.body.classList.remove('no-scroll');
      }
    },
    [openToolBar],
  );

  React.useEffect(() => {
    const isBusinessPage = route.pathname.startsWith('/business');
    setIsBusiness(isBusinessPage);

    if (isBusinessPage) {
      document.documentElement.classList.add('business');
    } else {
      document.documentElement.classList.remove('business');
    }
  }, [route.pathname]);

  const handleBusinessLogin = () => {
    return route.asPath === '/login'
      ? window.open('https://my.bakcell.com/', '_blank')
      : route.push('/business');
  };

  const buttonsSwitch = [
    {
      text: t('Personal'),
      onClick: async () => {
        overflowDocument(false);
        document.documentElement.classList.remove('business');
        await route.push('/');
        setOpenMenu(false);
      },
    },
    {
      text: t('Business'),
      onClick: async () => {
        overflowDocument(false);
        document.documentElement.classList.add('business');
        await handleBusinessLogin();
        setOpenMenu(false);
      },
    },
  ];

  useEffect(() => {
    setTopIconTheme(
      theme === ETheme.LIGHT_MODE ? 'light_mode_icon' : 'dark_mode_icon',
    );
  }, [theme]);

  const renderBundle = (item, index) => {
    return (
      <button
        key={index}
        onClick={() => handleActions(item, index)}
        aria-label='Bundle button'
      >
        <Image
          src={item?.light_mode_icon}
          alt='Bundle item image'
          title={item?.text}
          className={styles.gray_icon}
          width={20}
          height={20}
          loading='lazy'
          aria-label={item?.text}
        />
        {item?.text}
      </button>
    );
  };

  const renderMobileBundle = (item, index) => {
    return (
      <button
        key={index}
        onClick={() => handleActions(item, index)}
        aria-label='Mobile bundle button'
      >
        <Image
          src={item?.[topIconTheme]}
          alt='Bundle mobile item image'
          title={item?.text}
          className={styles.gray_icon}
          width={20}
          height={20}
          loading='lazy'
          aria-label={item?.text}
        />
        {item?.text}
      </button>
    );
  };

  const renderTopLinks = (item, index) => {
    return (
      <li
        key={`${index}_renderTopLinks`}
        itemScope
        itemType='https://schema.org/SiteNavigationElement'
      >
        {item?.slug ? (
          <ActiveLink href={item?.slug} handleOpenMenu={handleOpenMenu}>
            <a
              title={item?.text}
              className={classNames({
                [styles.active]:
                  route.asPath === '/about-us' && item.slug === 'about-us',
              })}
            >
              {item?.text}
            </a>
          </ActiveLink>
        ) : (
          <a
            href={item?.external_link}
            title={item?.text}
            target='_blank'
            rel='noreferrer'
          >
            {item?.text}
          </a>
        )}
      </li>
    );
  };

  const renderHeaderIntro = (item, index) => {
    return (
      <div
        className={styles.intro__item}
        key={`${index}_renderHeaderIntro`}
        itemScope
        itemType='https://schema.org/Organization'
      >
        <Logo width={17} />
        <Image
          src={marqueeData?.marquee_icon}
          alt='marquee icon'
          title='Marquee'
          width={17}
          height={17}
          layout='responsive'
          loading='lazy'
        />
        <p className={styles.text}>{marqueeData?.marquee_text}</p>
      </div>
    );
  };

  const renderBottomNav = (item, index) => {
    return (
      <NavItem
        item={item}
        key={index}
        handleOpenMenu={handleOpenMenu}
        itemScope
        itemType='https://schema.org/SiteNavigationElement'
      />
    );
  };

  const handleActions = (data) => {
    if (data?.type === 'external') {
      setTopUpModalActive(true);
      return;
    }
    if (data?.tariff_choice?.length) {
      setFindYourTariffModalData(data?.tariff_choice);
      setFindYourTariffModal(true);
      return;
    }

    if (data?.type === 'external') {
      window?.open(data?.external_link);
      return;
    }
    handleOpenMenu();
    route.push(`/${data?.slug}`);
  };

  const joinBakcellHandler = () => {
    if (!isBusiness) {
      setOpenJoinBackcellModal(true);
    }
  };

  let headerIndex = 0;

  if (route.asPath.includes('/about-us')) {
    headerIndex = null;
  } else if (route.asPath.includes('/business')) {
    headerIndex = 1;
  }
  return (
    <>
      <div
        className={classNames([
          styles.introWrapper,
          { [styles.business]: isBusiness },
        ])}
        itemScope
        itemType='https://schema.org/Property'
      >
        {marqueeData?.show_marquee && (
          <div className={styles.intro}>
            {[...Array(20)]?.map(renderHeaderIntro)}
          </div>
        )}
      </div>
      {toolbar && (
        // <Portal>
        <ToolBar
          active={openToolBar}
          setActive={handleOpenToolBar}
          data={toolbar}
        />
        // </Portal>
      )}
      <header
        className={classNames([
          styles.header,
          { [styles.open]: openMenu },
          { [styles.business]: isBusiness },
        ])}
        itemScope
        itemType='https://schema.org/WPHeader'
      >
        <div
          className={classNames(styles.header__content, styles.wrapper)}
          itemScope
          itemType='https://schema.org/Organization'
        >
          <div
            className={classNames(
              styles.mobile,
              'wrapper',
              isCabinetPage ? styles.cabinetPage : '',
            )}
          >
            <div className={styles.left}>
              <Button
                variant={'text'}
                size={'normal'}
                Component={isBusiness ? 'router' : 'button'}
                className={styles.joinBakcell}
                href={'/business/join-us'}
                onClick={joinBakcellHandler}
                data-testid={'join-bakcell'}
              >
                {join?.menu_button_text}
              </Button>
              <button
                aria-label={'Bakcell'}
                className={styles.burgerHolder}
                onClick={handleOpenMenu}
              >
                {/* <span className={styles.burger} /> */}
                {/* <img src='/Burger.svg' alt='burger' /> */}
                <BurgerSvg />
              </button>
            </div>
            <div className={styles.right}>
              <ActiveLink
                // handleOpenMenu={handleOpenMenu}
                href={isBusiness ? '/business' : '/'}
              >
                <a title='Bakcell' aria-label='Bakcell' className={styles.logo}>
                  <div className={styles.bakcell_logo}>
                    <Image
                      src={'/data/new-logo.svg'}
                      alt={'Bakcell'}
                      title={'Bakcell'}
                      width={84}
                      height={16}
                    />
                  </div>
                </a>
              </ActiveLink>
            </div>
          </div>
          <div className={styles.navContent}>
            <div className={styles.wrapper}>
              <div className={styles.top}>
                <div className={styles.topContent}>
                  <div className={`${styles.left} flex-c wrapper`}>
                    <ButtonsSwitch
                      className={styles.pageSwitch}
                      items={buttonsSwitch}
                      currentIndex={headerIndex}
                    />
                    {topLinks?.length && (
                      <ul className={styles.list}>
                        {topLinks?.map(renderTopLinks)}
                      </ul>
                    )}
                    <div className={styles.mobileButtons}>
                      <Language />
                      <button
                        className={`${styles.button} ${styles.closeMobile}`}
                        onClick={handleOpenMenu}
                        aria-label={'Bakcell'}
                      >
                        <CloseMobile />
                      </button>
                    </div>
                  </div>
                  <div className={styles.right}>
                    <div className={styles.buttons}>
                      {bundle?.map(renderBundle)}
                    </div>
                    <div className={styles.mobileWrapper}>
                      {(!loggedIn || isBusiness) && (
                        <Button
                          className={styles.singIn}
                          variant={'border'}
                          size={'normal'}
                          Component={'a'}
                          href={`${
                            isBusiness ? 'https://my.bakcell.com/' : '/login'
                          }`}
                        >
                          {t('Sign in')}
                        </Button>
                      )}
                      <button
                        onClick={handleOpenToolBar}
                        className={styles.menu}
                        aria-label='Bakcell'
                      >
                        <Menu />
                      </button>
                    </div>

                    <ul className={styles.list}>
                      <li>
                        <Language />
                      </li>
                      <li>
                        <DayNightSwitch />
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div
                className={`${styles.bottom} ${
                  isCabinetPage ? styles.cabinetPage : ''
                }`}
              >
                {openSearch && (
                  <SearchField
                    active={openSearch}
                    setActive={setOpenSearch}
                    setOpenMenu={setOpenMenu}
                  />
                )}
                {windowsX < 1025 && (
                  <InputAutoComplete
                    className={classNames(styles.mobile_input, {
                      [styles.disabled]: openSearch,
                    })}
                    placeholder={t('Search')}
                    endIcon={<Search />}
                    onClick={() => setOpenSearch(true)}
                  />
                )}
                <div className={styles.wrapper}>
                  <div className={styles.left}>
                    <ActiveLink
                      href={isBusiness ? '/business' : '/'}
                      handleOpenMenu={handleOpenMenu}
                    >
                      <a
                        title='Bakcell'
                        aria-label='Bakcell'
                        className={styles.logo}
                      >
                        <div className={styles.bakcell_logo}>
                          <Image
                            src={'/data/new-logo.svg'}
                            alt={'Bakcell'}
                            title={'Bakcell'}
                            width={84}
                            height={16}
                          />
                        </div>
                      </a>
                    </ActiveLink>
                    <nav className={styles.navBar}>
                      {navigation?.map(renderBottomNav)}
                    </nav>
                    {!!windowsX && windowsX <= 1024 && (
                      <div className={styles.buttons}>
                        {bundle?.map(renderMobileBundle)}
                      </div>
                    )}
                  </div>
                  <ul className={styles.right}>
                    <li>
                      <button
                        className={styles.search}
                        onClick={() => setOpenSearch(!openSearch)}
                      >
                        <Search />
                      </button>
                    </li>
                    {!isBusiness && toolbar && (
                      <>
                        <li>
                          <button
                            onClick={handleOpenToolBar}
                            className={styles.menu}
                            aria-label='Bakcell'
                          >
                            <Menu />
                          </button>
                        </li>
                      </>
                    )}

                    <li className={styles.contacts}>
                      {user ? (
                        <PhoneNumbersDropDown isBusiness={isBusiness} />
                      ) : (
                        <>
                          <Button
                            variant={'text'}
                            size={'normal'}
                            Component={isBusiness ? 'router' : 'button'}
                            className={styles.joinBakcell}
                            href={'/business/join-us'}
                            onClick={() => {
                              handleOpenMenu();
                              if (!isBusiness) {
                                setOpenJoinBackcellModal(true);
                              }
                            }}
                          >
                            {join?.menu_button_text}
                          </Button>

                          {loggedIn && !isBusiness ? (
                            <div
                              onClick={() => push('/cabinet')}
                              className={styles.userIcon}
                            >
                              <div>
                                <div className={styles.userIconDiv}>
                                  <User />
                                </div>
                                <span>{cabinet('breadcrumb.title')}</span>
                              </div>
                              <div className={styles.arrowDiv}>
                                <ArrowRightNew
                                  className={styles.arrowIconNew}
                                />
                              </div>
                            </div>
                          ) : (
                            <Button
                              className={styles.singIn}
                              variant={'border'}
                              size={'normal'}
                              Component={'a'}
                              href={`${
                                isBusiness
                                  ? 'https://my.bakcell.com/'
                                  : '/login'
                              }`}
                            >
                              {t('Sign in')}
                            </Button>
                          )}
                        </>
                      )}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      {topUpBalanceModal && (
        <TopUpBalanceModal
          user={user}
          setActive={setTopUpBalanceModal}
          active={topUpBalanceModal}
          onLogin={onLogin}
        />
      )}
      {openLoginModal && (
        <LoginModal
          setUser={setUser}
          active={openLoginModal}
          setActive={setOpenLoginModal}
        />
      )}
      {openJoinBackcellModal && (
        <JoinBackcellModal
          join={join}
          active={openJoinBackcellModal}
          setActive={setOpenJoinBackcellModal}
          data-testid={'join-bakcell-modal'}
        />
      )}
      {!!findYourTariffModalData && (
        <FindYourTariffModal
          data={findYourTariffModalData}
          active={findYourTariffModal}
          setActive={setFindYourTariffModal}
        />
      )}
      <TariffCardPopup />
      {topUpModalActive && (
        <TopUpModal
          active={topUpModalActive}
          setTopUpModalActive={() => setTopUpModalActive(false)}
          isMainPage={true}
        />
      )}
    </>
  );
};

export default Header;
